/**
 * Fraza de recuperare — a doua cheie a contului, știută doar de user.
 *
 * La pierderea parolei master cheia privată nu mai poate fi decriptată, deci
 * tot ce avea userul devine inaccesibil. Fraza rezolvă asta fără să dea
 * serverului (sau unui admin) vreo cale de acces:
 *
 *   recovery_key       = Argon2id(frază, recovery_salt)
 *   recovery_auth_hash = SHA-256(recovery_key || "recovery-v1")   → server (bcrypt)
 *   blob               = encryptSym(private_key, recovery_key)     → server
 *
 * Fraza: 12 cuvinte din lista BIP39 engleză (128 biți de entropie). Un cuvânt
 * tastat greșit, care nu e în listă, e respins înainte de derivare; suma de
 * control (4 biți) prinde în plus cam 15 din 16 înlocuiri cu alt cuvânt valid.
 * Verificarea finală rămâne oricum la server și la decriptarea blob-ului. Generarea
 * și validarea vin din `@scure/bip39`; derivarea cheii rămâne pe Argon2id din
 * `sodium.ts`, nu pe PBKDF2-ul din BIP39.
 */
import { generateMnemonic, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';

import {
  bytesToBase64,
  bytesToStr,
  DEFAULT_KDF_PARAMS,
  decryptSym,
  deriveMasterKey,
  encryptSym,
  generateSalt,
  type KeyPair,
  strToBytes,
  unwrapKey,
  wrapKey,
} from '@/crypto/sodium';

export const RECOVERY_PHRASE_WORDS = 12;
const AUTH_CONTEXT = 'recovery-v1';

export function generateRecoveryPhrase(): string {
  return generateMnemonic(wordlist, 128);
}

/** Litere mici, un singur spațiu între cuvinte — toleranță la cum tastează userul. */
export function normalizeRecoveryPhrase(input: string): string {
  return input.trim().toLowerCase().split(/\s+/).filter(Boolean).join(' ');
}

export function isValidRecoveryPhrase(input: string): boolean {
  return validateMnemonic(normalizeRecoveryPhrase(input), wordlist);
}

export async function deriveRecoveryKey(
  phrase: string,
  salt: Uint8Array,
  params: { opslimit: number; memlimit: number } = DEFAULT_KDF_PARAMS,
): Promise<Uint8Array> {
  return deriveMasterKey(normalizeRecoveryPhrase(phrase), salt, params);
}

/** Dovada trimisă serverului: nu permite refacerea `recovery_key`. */
export async function computeRecoveryAuthHash(recoveryKey: Uint8Array): Promise<Uint8Array> {
  const suffix = strToBytes(AUTH_CONTEXT);
  const input = new Uint8Array(recoveryKey.length + suffix.length);
  input.set(recoveryKey, 0);
  input.set(suffix, recoveryKey.length);
  return new Uint8Array(await crypto.subtle.digest('SHA-256', input));
}

export interface RecoverySetup {
  salt: Uint8Array;
  authHash: Uint8Array;
  privateKeyEncrypted: Uint8Array;
  privateKeyNonce: Uint8Array;
  /** Fraza sigilată cu cheia publică a userului — ca s-o poată revedea din cont. */
  phraseSealed: Uint8Array;
}

/**
 * Tot ce trebuie trimis serverului pentru o frază nouă (la înregistrare sau
 * la `/auth/recovery/setup`).
 */
export async function buildRecoverySetup(
  phrase: string,
  keyPair: KeyPair,
): Promise<RecoverySetup> {
  const salt = await generateSalt();
  const recoveryKey = await deriveRecoveryKey(phrase, salt);
  try {
    const sealed = await encryptSym(keyPair.privateKey, recoveryKey);
    return {
      salt,
      authHash: await computeRecoveryAuthHash(recoveryKey),
      privateKeyEncrypted: sealed.ciphertext,
      privateKeyNonce: sealed.nonce,
      phraseSealed: await sealRecoveryPhrase(phrase, keyPair.publicKey),
    };
  } finally {
    recoveryKey.fill(0);
  }
}

/** Forma trimisă serverului (register, change-password, recovery/setup). */
export async function buildRecoveryBundle(phrase: string, keyPair: KeyPair) {
  const setup = await buildRecoverySetup(phrase, keyPair);
  return {
    recovery_kdf_salt: bytesToBase64(setup.salt),
    recovery_auth_hash: bytesToBase64(setup.authHash),
    recovery_private_key_encrypted: bytesToBase64(setup.privateKeyEncrypted),
    recovery_private_key_nonce: bytesToBase64(setup.privateKeyNonce),
    recovery_phrase_sealed: bytesToBase64(setup.phraseSealed),
  };
}

/**
 * Sigilează fraza cu cheia publică a userului (`crypto_box_seal`). O poate
 * deschide doar cheia lui privată, care nu se schimbă la schimbarea parolei
 * sau la recuperare — deci blob-ul nu trebuie re-criptat niciodată.
 */
export async function sealRecoveryPhrase(
  phrase: string,
  publicKey: Uint8Array,
): Promise<Uint8Array> {
  return wrapKey(strToBytes(normalizeRecoveryPhrase(phrase)), publicKey);
}

export async function openSealedRecoveryPhrase(
  sealed: Uint8Array,
  keyPair: KeyPair,
): Promise<string> {
  return bytesToStr(await unwrapKey(sealed, keyPair));
}

/** Decriptează copia cheii private cu `recovery_key`. Aruncă dacă fraza e greșită. */
export async function openRecoveryBlob(
  recoveryKey: Uint8Array,
  privateKeyEncrypted: Uint8Array,
  privateKeyNonce: Uint8Array,
): Promise<Uint8Array> {
  return decryptSym({ ciphertext: privateKeyEncrypted, nonce: privateKeyNonce }, recoveryKey);
}
